import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { storyQueryKeys } from './stories';

/**
 * Phase 1 Dedup T17 — Story WebSocket subscription.
 *
 * Opens a WebSocket to the same-origin `/api/ws` endpoint and listens for
 * `story:created`, `story:member_added`, `story:updated`, and `story:archived`
 * events. On each event, invalidates the public story caches (`storyQueryKeys.all`)
 * and the admin story cache (`['admin', 'stories']` — decoupled from `admin-stories.ts`
 * on purpose per T3's comment). Events carrying a `storyId` additionally invalidate
 * that story's detail + sources queries so an open detail view refreshes.
 *
 * Backend helpers live in `apps/api/src/ws.ts` (`emitStoryCreated`,
 * `emitStoryMemberAdded`, `emitStoryUpdated`, `emitStoryArchived`). Those helpers
 * broadcast with `JSON.stringify({ type, data })` where `data` is the
 * `{ storyId, articleId? }` payload, so we look for the storyId under both
 * `msg.storyId` (plan shape) and `msg.data.storyId` (actual backend shape).
 *
 * The effect is resilient to the endpoint being unreachable — `ws.close()` in
 * cleanup always runs, and non-JSON / unknown frames are silently ignored.
 */
type StoryEventType =
  | 'story:created'
  | 'story:member_added'
  | 'story:updated'
  | 'story:archived';

interface StoryEventMessage {
  type?: StoryEventType | string;
  storyId?: string;
  data?: { storyId?: string; articleId?: string };
}

const STORY_EVENT_TYPES: ReadonlySet<string> = new Set([
  'story:created',
  'story:member_added',
  'story:updated',
  'story:archived',
]);

export function useStorySubscription(enabled: boolean): void {
  const qc = useQueryClient();

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined') return;

    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = `${proto}//${window.location.host}/api/ws`;
    const ws = new WebSocket(url);

    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data) as StoryEventMessage;
        if (!msg.type || !STORY_EVENT_TYPES.has(msg.type)) return;

        qc.invalidateQueries({ queryKey: storyQueryKeys.all });
        qc.invalidateQueries({ queryKey: ['admin', 'stories'] });

        const storyId = msg.storyId ?? msg.data?.storyId;
        if (storyId) {
          qc.invalidateQueries({ queryKey: storyQueryKeys.detail(storyId) });
          qc.invalidateQueries({ queryKey: storyQueryKeys.sources(storyId) });
        }
      } catch {
        // non-JSON frames are fine to ignore
      }
    };

    return () => {
      ws.close();
    };
  }, [enabled, qc]);
}
